import is from '@sindresorhus/is';
import { first } from 'lodash';
import type { FunctionComponent } from 'react';
import { useContext, useMemo } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { useHasRole } from '~/shared/context/use-has-role';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { getJobTypeTranslation } from '../hooks/use-review-action';
import {
    getNewerVersions,
    getPublishedVersionInfo,
    hasReviewedOrPublishedVersion,
} from './notification-bar/notification-version-utils';
import { ArchivedPrompt, AssignReviewerPrompt, GeneratingPrompt, PublishPrompt, RoleReviewPrompt } from './prompts';
import type { PromptKind } from './resolve-prompt-kind';
import { resolvePromptKind } from './resolve-prompt-kind';

type ItemType = 'job-level' | 'job-role';

type JobProfileReviewPromptProps = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: ItemType;
    readonly version?: number;
    readonly preferredLanguage?: LanguageCodeForJSA;
};

// Hidden when a newer version is reviewed/published (same guard as notification bars).
export const JobProfileReviewPrompt: FunctionComponent<JobProfileReviewPromptProps> = ({
    architectureId,
    itemId,
    itemType,
    version,
}) => {
    const { translate } = useContext(ApplicationContext);
    const isJobArchitect = useHasRole('job_architect');

    const { data: versions, isLoading: isLoadingVersions } = useJobItemVersions({
        architectureId,
        itemId,
        itemType,
    });

    const activeVersion = is.number(version) ? version.toString() : first(versions)?.version.toString();

    const { data: item } = useJobItemDetails({
        architectureId,
        enabled: !isLoadingVersions,
        itemId,
        itemType,
        version: activeVersion,
    });

    const isStaleVersion = useMemo(() => {
        const newerVersions = getNewerVersions(versions, item?.versionInfo?.version);
        return hasReviewedOrPublishedVersion(newerVersions);
    }, [versions, item?.versionInfo?.version]);

    const { hasPreviousPublishedVersion, lastPublishedVersion } = useMemo(
        () => getPublishedVersionInfo(versions),
        [versions],
    );
    const isNewVersion = hasPreviousPublishedVersion;
    const currentVersion = item?.versionInfo?.version;
    const previousVersion = lastPublishedVersion?.version;

    const kind = useMemo<PromptKind>(() => resolvePromptKind(item, isJobArchitect), [item, isJobArchitect]);

    if (item == null || kind == null || isStaleVersion) {
        return null;
    }

    const jobType = getJobTypeTranslation(itemType, translate);
    const title = item.title;

    switch (kind) {
        case 'generating':
            return <GeneratingPrompt jobType={jobType} />;
        case 'archived':
            return <ArchivedPrompt architectureId={architectureId} item={item} itemType={itemType} jobType={jobType} />;
        case 'assign-reviewer':
            return (
                <AssignReviewerPrompt
                    architectureId={architectureId}
                    item={item}
                    itemType={itemType}
                    jobType={jobType}
                />
            );
        case 'role-review':
            return (
                <RoleReviewPrompt
                    architectureId={architectureId}
                    currentVersion={currentVersion}
                    isNewVersion={isNewVersion}
                    item={item}
                    itemType={itemType}
                    previousVersion={previousVersion}
                    title={title}
                />
            );
        case 'publish':
            return (
                <PublishPrompt
                    architectureId={architectureId}
                    currentVersion={currentVersion}
                    isNewVersion={isNewVersion}
                    item={item}
                    itemType={itemType}
                    jobType={jobType}
                    previousVersion={previousVersion}
                    title={title}
                />
            );
    }
};
